// ---------------------------------------------------------------------------
// SEARCH ENRICHMENT TELEMETRY
//
// Why this exists (Morgan, Aug 24): we could not answer "of searches that
// degrade because they miss the ready ceiling, what percentage finish within
// +5 seconds of the deadline?" — because nothing persisted `elapsedMs` or
// `budgetMs`. They lived only in a console line. Every candidate table was
// checked; none carried search timing. The shadow-worker timeout data was
// explicitly ruled out as a proxy.
//
// This module writes ONE row per completed search into
// `public.search_enrichment_telemetry`, and does nothing else. In particular:
//
//   IT DOES NOT CHANGE THE BUDGET. Not the ~23s ready ceiling, not the pass-1
//   or pass-2 budgets, not a timeout anywhere. It observes and reports. If a
//   later change to this file alters what a search DOES, that is a bug in the
//   change, not a feature of the instrumentation.
//
// PRIVACY. No user id, no session id, no auth token, no coordinates, no
// origin, no destination name. `region` is a 1-degree grid label — roughly a
// 110 km cell, coarse enough that it identifies a metro area and nothing
// finer. That is the minimum granularity that answers the actual question,
// which is geographic: a search in Denver and a search in Boston degrade for
// different reasons (the private Overpass box holds the us-northeast extract
// only) and a single global average hides that completely.
//
// FAILURE IS SILENT AND FREE. Every write is fire-and-forget behind a catch.
// Telemetry must never fail a search, delay a search, or throw into one.
// ---------------------------------------------------------------------------

import { supabase } from "@/integrations/supabase/client";
import { appVersion } from "@/lib/env";

export type EnrichmentVerdict = "enriched" | "degraded" | "unmeasured";
export type Coverage = "COMPLETE" | "PARTIAL" | "UNMEASURED";
export type EnrichmentPath = "private" | "public-mirror" | "edge" | "road-index" | "none";
// Imported rather than redeclared. A second copy of this union here is exactly
// the kind of quiet drift that lets a new mode reach production with telemetry
// silently dropping it. Type-only import — erased at build.
export type { TravelMode } from "@/api/routing";
import type { TravelMode } from "@/api/routing";

// --- cumulative process-wide counters, sampled by delta ---------------------
// A single search fans out into many `fetchOverpassTiles` calls across two
// passes plus discovery. Rather than thread a search id through the whole
// Overpass layer (invasive, and it would touch the code paths we are trying
// not to disturb), the counters are cumulative and each search takes a
// before/after delta. With one search in flight this is exact. With two, the
// attribution blurs — which is precisely what `concurrent_searches_in_flight`
// is recorded for, so the analysis can drop or separate those rows.

let CELLS_REQUESTED = 0;
let CELLS_SERVED = 0;
const PATH_HITS: Record<EnrichmentPath, number> = {
  private: 0, "public-mirror": 0, edge: 0, "road-index": 0, none: 0,
};

let IN_FLIGHT = 0;

/** Called by osm.ts once per tile batch. Cheap, synchronous, no I/O. */
export function noteOverpassCells(requested: number, served: number): void {
  if (Number.isFinite(requested) && requested > 0) CELLS_REQUESTED += requested;
  if (Number.isFinite(served) && served > 0) CELLS_SERVED += served;
}

/** Called by osm.ts when an endpoint actually answers (or all of them fail). */
export function noteEnrichmentPath(path: EnrichmentPath): void {
  if (path in PATH_HITS) PATH_HITS[path] += 1;
}

export interface SearchTelemetryHandle {
  cells0: number;
  served0: number;
  path0: Record<EnrichmentPath, number>;
  /** In-flight count INCLUDING this search, at the moment it started. */
  concurrentAtStart: number;
  closed: boolean;
}

/** Open a telemetry window. Must be paired with exactly one recordSearch(). */
export function beginSearch(): SearchTelemetryHandle {
  IN_FLIGHT += 1;
  return {
    cells0: CELLS_REQUESTED,
    served0: CELLS_SERVED,
    path0: { ...PATH_HITS },
    concurrentAtStart: IN_FLIGHT,
    closed: false,
  };
}

/**
 * A 1-degree grid label. Deliberately coarse — see the privacy note above.
 * `null` in, `null` out; we never invent a location we were not given.
 */
export function coarseRegion(lat?: number | null, lng?: number | null): string | null {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `${Math.floor(lat)},${Math.floor(lng)}`;
}

/** Which endpoint carried this search, by whichever answered most often. */
function dominantPath(h: SearchTelemetryHandle): EnrichmentPath | null {
  let best: EnrichmentPath | null = null;
  let bestN = 0;
  for (const k of Object.keys(PATH_HITS) as EnrichmentPath[]) {
    const n = PATH_HITS[k] - (h.path0[k] ?? 0);
    if (n > bestN) { bestN = n; best = k; }
  }
  // No endpoint answered at all during this search — that is itself the answer.
  if (best === null && h.cells0 !== CELLS_REQUESTED) return "none";
  return best;
}

export interface RecordSearchArgs {
  handle: SearchTelemetryHandle;
  /** The ceiling this search was actually held to, in ms. Observed, not set. */
  budgetMs: number;
  /** performance.now() - T0 at the terminal point, in ms. */
  elapsedMs: number;
  verdict: EnrichmentVerdict;
  coverage?: Coverage | null;
  /** Distance of the route the cards were built around, in metres. */
  routeDistanceM?: number | null;
  mode?: TravelMode | null;
  /** Start coordinates — used ONLY to derive the 1-degree region label. */
  lat?: number | null;
  lng?: number | null;
}

/**
 * Close the window and write the row. Never throws, never awaits into the
 * caller's critical path — call it and move on.
 */
export function recordSearch(args: RecordSearchArgs): void {
  const { handle: h } = args;
  // Double-close would corrupt the in-flight count, which is the one number
  // here that cannot be recovered after the fact.
  if (h.closed) return;
  h.closed = true;
  IN_FLIGHT = Math.max(0, IN_FLIGHT - 1);

  try {
    const cellsRequested = Math.max(0, CELLS_REQUESTED - h.cells0);
    const cellsServed = Math.max(0, CELLS_SERVED - h.served0);
    // Same three-way verdict osm.ts logs per tile batch, aggregated over the
    // whole search. Rule 20 applies: served=0 is UNMEASURED — "we could not
    // look here" — never "there is nothing here". If no cells were requested
    // at all, coverage is not UNMEASURED either; it is simply unknown, so the
    // column stays null rather than asserting a verdict nobody reached.
    const coverage: Coverage | null =
      args.coverage ??
      (cellsRequested === 0 ? null
        : cellsServed === 0 ? "UNMEASURED"
        : cellsServed < cellsRequested ? "PARTIAL"
        : "COMPLETE");
    // Both are NOT NULL in the table. A non-finite value would serialise to
    // null and the row would be silently rejected — losing exactly the search
    // we most wanted to see. Clamp instead.
    const ms = (v: number) => (Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0);
    const row = {
      budget_ms: ms(args.budgetMs),
      elapsed_ms: ms(args.elapsedMs),
      enrichment_verdict: args.verdict,
      coverage,
      cells_requested: cellsRequested,
      cells_served: cellsServed,
      route_distance_m:
        typeof args.routeDistanceM === "number" && Number.isFinite(args.routeDistanceM)
          ? Math.round(args.routeDistanceM)
          : null,
      travel_mode: args.mode ?? null,
      region: coarseRegion(args.lat, args.lng),
      concurrent_searches_in_flight: Math.min(32767, h.concurrentAtStart),
      enrichment_path: dominantPath(h),
      // Was `import.meta.env.VITE_APP_VERSION`, which is set nowhere and
      // therefore wrote null on every row while route_feedback wrote a
      // (stale) literal. Two sources for one fact, both wrong differently.
      app_version: appVersion(),
    };

    // The generated Supabase types predate this table; regenerate them and the
    // cast can go. Casting rather than blocking the instrumentation on a
    // codegen step.
    void (supabase as any)
      .from("search_enrichment_telemetry")
      .insert(row)
      .then(({ error }: { error: unknown }) => {
        if (error) {
          // eslint-disable-next-line no-console
          console.debug("[search-telemetry] insert failed (ignored)", error);
        }
      })
      .catch(() => { /* offline, blocked, RLS — never a search's problem */ });
  } catch {
    /* nothing here is allowed to break a search */
  }
}

/** Test seam only. Not called by app code. */
export function __resetSearchTelemetryForTests(): void {
  CELLS_REQUESTED = 0;
  CELLS_SERVED = 0;
  for (const k of Object.keys(PATH_HITS) as EnrichmentPath[]) PATH_HITS[k] = 0;
  IN_FLIGHT = 0;
}

/** Test seam only. */
export function __searchTelemetryCounters() {
  return {
    cellsRequested: CELLS_REQUESTED,
    cellsServed: CELLS_SERVED,
    inFlight: IN_FLIGHT,
    pathHits: { ...PATH_HITS },
  };
}
